import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useViewMode } from '@/contexts/ViewModeContext';
import { LEARNING, MANAGE, SHARED_LEARN, SHARED_MANAGE, isNavItemVisible, type NavItem } from '@/config/navigation';
import { PERM } from '@/auth/permissions';
import { useTheme } from '@/contexts/ThemeContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { useImageUrl } from '@/services/uploads';
import { useNotifications, countUnreadGroups } from '@/services';
import NotificationsPanel from '../common/NotificationsPanel';
import Footer from './Footer';
import AssistantLauncher from '../assistant/AssistantLauncher';
import TereikoIcon from '../../../public/logos/TereikoLogo.png';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  CheckIcon,
  XMarkIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowRightIcon,
  AcademicCapIcon,
  Squares2X2Icon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import MenuIcon from '@/components/icons/ManuIcon';
import NotificationsIcons from '@/components/icons/NotificationsIcons';
import SettingsIcon from '@/components/icons/SettingsIcon';
import PencilIcon from '@/components/icons/PencilIcon';
import ChatBadge from '../chat/ChatBadge';
import { ErrorBoundary } from '../common/ErrorBoundary';
import ReconciliationBanner from '../settings/billing/ReconciliationBanner';
import LegalAcknowledgementNotice from '../common/LegalAcknowledgementNotice';
import { migrateDismissals } from '@/utils/migrateDismissals';

interface AppLayoutProps {
  children: React.ReactNode;
}

const SIDEBAR_WIDTH = 280;
const SIDEBAR_COLLAPSED_WIDTH = 72;

// Hover chip shown under a header icon (parent needs `group relative`).
const HEADER_TIP_CLASS =
  'pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 ' +
  'whitespace-nowrap rounded-full bg-surface-elevated border border-border-primary/20 ' +
  'px-2.5 py-1 text-xs font-medium text-text-primary shadow-md z-50 ' +
  'opacity-0 group-hover:opacity-100 transition-opacity duration-fast';

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { collapsed: sidebarCollapsed, setCollapsed: setSidebarCollapsed } = useSidebar();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [workspaceMenuOpen, setWorkspaceMenuOpen] = useState(false);
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);
  const location = useLocation();
  const { user, logout, activeMembership, memberships, switchTenant } = useAuth();
  const { can } = usePermissions();
  const { mode, canManage, switchMode } = useViewMode();
  const { theme, setTheme, useSystemTheme, isSystemTheme } = useTheme();
  const [avatarUrl] = useImageUrl(user?.avatar_url ?? null);
  const { data: notificationsData } = useNotifications();
  // Count unread ROWS as the panel displays them (chat collapses to one row
  // per sender) — the raw server total reads as wrong next to the grouped
  // list (e.g. badge 10, panel 6).
  const unreadNotificationsCount = countUnreadGroups(notificationsData?.notifications ?? []);

  // One-time, best-effort copy of pre-existing localStorage reminder dismissals
  // into the member's server-side config, keyed (and guarded) per tenant.
  const activeTenantId = activeMembership?.tenant.id;
  // Workspace branding (Pro+): a custom logo replaces the Tereiko mark.
  const [brandLogoUrl] = useImageUrl(activeMembership?.tenant.styling?.logo_key ?? null);
  useEffect(() => {
    if (activeTenantId) void migrateDismissals(activeTenantId);
  }, [activeTenantId]);

  // A Manage item shows only when the membership holds its permission AND the
  // tenant's plan is in any tier list it declares (feature-tier gating).
  const plan = activeMembership?.tenant?.subscription_plan;
  const manageVisible = MANAGE.filter((item) => isNavItemVisible(item, can, plan));
  const learningVisible = LEARNING.filter((item) => isNavItemVisible(item, can, plan));
  const canSwitch = memberships.length > 1;

  // The sidebar shows a single surface at a time: Manage for staff in manage
  // mode, the universal Learning surface otherwise.
  const showManage = canManage && mode === 'manage';
  const activeNav: NavItem[] = showManage ? manageVisible : learningVisible;
  const sectionLabel = showManage ? 'Manage' : 'Learning';
  // Universal tools (Chat, Calendar) shown in both modes, pointing at the
  // surface that matches the current mode so the link never flips the mode.
  const sharedNav: NavItem[] = (showManage ? SHARED_MANAGE : SHARED_LEARN).filter(
    (item) => isNavItemVisible(item, can, plan)
  );

  // Avatar-menu toggle target (only meaningful for staff).
  const switchTarget = mode === 'manage' ? 'learn' : 'manage';
  const switchLabel = switchTarget === 'manage' ? 'Manage' : 'Learning';

  const handleModeSwitch = () => {
    setUserMenuOpen(false);
    switchMode(switchTarget);
  };

  const isActive = (path: string) => {
    if (path === '/learn/dashboard') return location.pathname === path;
    if (path === '/courses') {
      return (
        location.pathname === '/courses' ||
        (location.pathname.startsWith('/courses/') &&
          !location.pathname.startsWith('/courses/create'))
      );
    }
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  const handleSwitch = async (tenantId: string) => {
    setWorkspaceMenuOpen(false);
    if (tenantId === activeMembership?.tenant.id) return;
    await switchTenant(tenantId);
  };

  const sidebarW = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.path);
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setMobileSidebarOpen(false)}
        title={sidebarCollapsed ? item.name : undefined}
        className={`
          flex items-center min-h-[44px] rounded-lg transition-colors group
          ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 pl-3 pr-2 py-1'}
          ${
            active
              ? 'bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300'
              : 'text-text-secondary hover:bg-neutral-100 dark:hover:bg-neutral-800/50 hover:text-text-primary'
          }
        `}
      >
        <span
          className={`relative shrink-0 ${
            active
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-text-secondary group-hover:text-text-primary'
          }`}
        >
          {item.icon}
        </span>
        {!sidebarCollapsed && (
          <>
            <span className="flex-1 text-sm font-medium truncate">{item.name}</span>
            {(item.path === '/chat' || item.path === '/learn/chat') && (
              <ChatBadge positioned={false} className="static" />
            )}
          </>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background-primary">
      {/* Mobile sidebar backdrop */}
      <AnimatePresence>
        {mobileSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 bottom-0 z-50 flex flex-col
          border-r border-border-secondary bg-background-primary overflow-hidden
          ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{
          width: mobileSidebarOpen ? SIDEBAR_WIDTH : sidebarW,
          transition:
            'width 300ms cubic-bezier(0.23, 1, 0.32, 1), transform 300ms cubic-bezier(0.23, 1, 0.32, 1)',
        }}
      >
        {/* Logo — home for the CURRENT mode (Aleix, 2026-09-10). It once linked
            unconditionally to /learn/dashboard, which flipped admins onto the
            learner surface with no matching way back; mode switching still
            belongs to the ViewMode switcher, so this only ever goes to the
            home of the surface you are already on. */}
        <Link
          to={showManage && can(PERM.ANALYTICS_VIEW) ? '/dashboard' : '/learn/dashboard'}
          onClick={() => setMobileSidebarOpen(false)}
          aria-label="Go to dashboard"
          className={`flex items-center pt-6 pb-2 shrink-0 rounded-lg focus-ring transition-opacity hover:opacity-80 ${sidebarCollapsed ? 'justify-center px-2' : 'px-[28px]'}`}
        >
          {/* Logo swaps by theme: the light art bakes in the light-mode purple;
              the dark art uses dark-mode purple (#9C7CE8) and keeps its own
              white negative-space detail. Runtime /logos path so a missing
              file degrades gracefully rather than breaking the build. */}
          <img
            src={brandLogoUrl ?? (theme === 'dark' ? '/logos/TereikoLogoDark.png' : TereikoIcon)}
            alt={brandLogoUrl ? (activeMembership?.tenant.name ?? 'Workspace') : 'Tereiko'}
            className="w-10 h-10 object-contain"
          />
        </Link>

        {/* Create Course — manage-mode CTA for course authors (restored from the
            legacy admin sidebar; sits just below the logo). */}
        {showManage && can(PERM.COURSE_AUTHOR) && (
          <div className={`shrink-0 ${sidebarCollapsed ? 'px-2' : 'px-4'} mb-2`}>
            <Link
              to="/courses/create"
              onClick={() => setMobileSidebarOpen(false)}
              title={sidebarCollapsed ? 'Create Course' : undefined}
              className={` flex items-center min-h-[44px] rounded-lg bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 pl-3 pr-2 py-1'} `}
            >
              <span className="shrink-0">
                <PencilIcon className="w-6 h-6" />
              </span>
              {!sidebarCollapsed && (
                <span className="flex-1 text-sm font-medium truncate">Create Course</span>
              )}
            </Link>
          </div>
        )}

        {/* Navigation — a single surface at a time (Manage or Learning); the
            avatar-menu toggle swaps modes for staff. */}
        <nav
          className={`flex-1 overflow-y-auto overflow-x-hidden pb-10 scrollbar-thin ${sidebarCollapsed ? 'px-2' : 'px-4'}`}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={showManage ? 'manage' : 'learning'}
              initial={{ opacity: 0, x: showManage ? 10 : -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, transition: { duration: 0.1 } }}
              transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            >
              {!sidebarCollapsed && (
                <div className="pt-4 pb-2 pl-3">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-text-secondary">
                    {sectionLabel}
                  </span>
                </div>
              )}
              {sidebarCollapsed && (
                <div className="pt-4 pb-2">
                  <div className="mx-auto w-6 border-t border-border/20" />
                </div>
              )}
              <div className="flex flex-col gap-1">{activeNav.map(renderNavItem)}</div>
            </motion.div>
          </AnimatePresence>

          {/* Shared utilities — Chat & Calendar, available to every member. */}
          <div className="mt-4 pt-4 border-t border-border/20">
            <div className="flex flex-col gap-1">{sharedNav.map(renderNavItem)}</div>
          </div>
        </nav>

        {/* Mobile close button */}
        <button
          onClick={() => setMobileSidebarOpen(false)}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-secondary lg:hidden"
          aria-label="Close sidebar"
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      </aside>

      {/* Sidebar collapse toggle (desktop) */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className="hidden lg:flex fixed z-50 top-[27px] w-6 h-6 items-center justify-center rounded-full bg-background-primary border border-border-secondary"
        style={{ left: sidebarW - 13, transition: 'left 300ms cubic-bezier(0.23, 1, 0.32, 1)' }}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? (
          <ChevronRightIcon className="w-4 shrink-0 text-text-secondary" />
        ) : (
          <ChevronLeftIcon className="w-4 shrink-0 text-text-secondary" />
        )}
      </button>

      {/* Main content area */}
      <div
        className="flex flex-col min-h-screen lg:ml-[var(--sidebar-w)]"
        style={
          {
            '--sidebar-w': `${sidebarW}px`,
            transition: 'margin-left 300ms cubic-bezier(0.23, 1, 0.32, 1)',
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <header className="sticky top-0 z-40 h-[72px] bg-background-primary shrink-0">
          <div className="h-full px-6 lg:px-10 flex items-center justify-between">
            {/* Left: mobile menu + workspace switcher */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden p-2 -ml-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 text-text-secondary"
                aria-label="Open sidebar"
              >
                <MenuIcon className="w-6 h-6" />
              </button>

              {activeMembership && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => canSwitch && setWorkspaceMenuOpen((v) => !v)}
                    disabled={!canSwitch}
                    className={`flex items-center gap-2 py-2 px-2 rounded-full transition-colors ${
                      canSwitch
                        ? 'hover:bg-neutral-50 dark:hover:bg-neutral-800/50'
                        : 'cursor-default'
                    }`}
                    aria-label="Workspace"
                  >
                    <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold">
                        {activeMembership.tenant.name?.[0]?.toUpperCase() ?? 'T'}
                      </span>
                    </div>
                    <span
                      className="text-sm font-semibold text-text-primary hidden sm:inline truncate max-w-[320px] xl:max-w-none"
                      title={activeMembership.tenant.name}
                    >
                      {activeMembership.tenant.name}
                    </span>
                    {canSwitch && (
                      <ChevronUpDownIcon className="w-5 h-5 text-text-tertiary hidden sm:block" />
                    )}
                  </button>

                  <AnimatePresence>
                    {workspaceMenuOpen && canSwitch && (
                      <>
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setWorkspaceMenuOpen(false)}
                        />
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                          style={{ transformOrigin: 'top left' }}
                          className="absolute left-0 top-12 w-64 bg-surface-primary border border-border rounded-lg shadow-lg py-1 z-50"
                        >
                          {memberships.map((m) => {
                            const isCurrent = m.tenant.id === activeMembership.tenant.id;
                            return (
                              <button
                                key={m.membership_id}
                                type="button"
                                onClick={() => handleSwitch(m.tenant.id)}
                                className="flex items-center justify-between gap-2 w-full text-left px-4 py-2.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                              >
                                <span className="flex flex-col">
                                  <span className="font-medium text-text-primary truncate">
                                    {m.tenant.name}
                                  </span>
                                  <span className="text-xs text-text-secondary">{m.role}</span>
                                </span>
                                {isCurrent && (
                                  <CheckIcon className="w-4 h-4 text-primary-600 dark:text-primary-350 shrink-0" />
                                )}
                              </button>
                            );
                          })}
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Right: actions */}
            <div className="flex items-center gap-1.5">
              {/* Settings — header affordance, shown only to members who can
                  manage the workspace (replaces the old sidebar Settings item). */}
              {can(PERM.TENANT_MANAGE) && (
                <div className="relative group">
                  <Link
                    to="/settings"
                    aria-label="Account center"
                    className={`block p-2 rounded-full transition-colors active:scale-95 text-primary-500 hover:bg-primary-500/10 ${
                      isActive('/settings') ? 'bg-primary-500/10' : ''
                    }`}
                  >
                    <SettingsIcon className="w-6 h-6" accentColor="transparent" />
                  </Link>
                  <span className={HEADER_TIP_CLASS}>Account center</span>
                </div>
              )}

              <div className="relative group">
                <button
                  onClick={() => setNotificationsPanelOpen(true)}
                  className="p-2 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-text-secondary active:scale-95"
                  aria-label="Notifications"
                >
                  <NotificationsIcons className="w-6 h-6" />
                </button>
                <span className={HEADER_TIP_CLASS}>Notifications</span>
                {unreadNotificationsCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-secondary text-xs font-bold"
                    aria-label={`${unreadNotificationsCount} unread notifications`}
                    data-testid="notifications-unread-badge"
                  >
                    {unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount}
                  </span>
                )}
              </div>

              {/* Avatar + profile menu (includes theme toggle) */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center p-0.5 rounded-full border-2 border-primary-400 dark:border-primary-500 hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
                  aria-label="User menu"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={user?.full_name ?? ''}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary-200 dark:bg-primary-800 flex items-center justify-center text-primary-700 dark:text-primary-200 text-sm font-semibold">
                      {user?.first_name?.[0]}
                      {user?.last_name?.[0]}
                    </div>
                  )}
                </button>

                <AnimatePresence>
                  {userMenuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
                        style={{ transformOrigin: 'top right' }}
                        className="absolute right-0 top-12 w-56 bg-surface-primary border border-border rounded-lg shadow-lg py-1 z-50"
                      >
                        {/* Mode switch — staff only (pure learners have one surface) */}
                        {canManage && (
                          <div className="p-2 border-b border-border">
                            <button
                              type="button"
                              onClick={handleModeSwitch}
                              className="flex items-center justify-between w-full gap-2 px-3 py-2.5 rounded-full bg-primary-50 dark:bg-primary-950/40 text-primary-700 dark:text-primary-300 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors active:scale-[0.98]"
                              aria-label={`Switch to ${switchLabel}`}
                            >
                              <span className="flex items-center gap-2 text-sm font-semibold">
                                {switchTarget === 'manage' ? (
                                  <Squares2X2Icon className="w-5 h-5 shrink-0" />
                                ) : (
                                  <AcademicCapIcon className="w-5 h-5 shrink-0" />
                                )}
                                Switch to {switchLabel}
                              </span>
                              <ArrowRightIcon className="w-4 h-4 shrink-0" />
                            </button>
                          </div>
                        )}

                        <div className="px-4 py-3 border-b border-border">
                          <p className="text-sm font-semibold text-text-primary truncate">
                            {user?.full_name ?? `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()}
                          </p>
                          <p className="text-xs text-text-secondary truncate">{user?.email}</p>
                        </div>

                        {/* Theme switcher */}
                        <div className="px-4 py-2.5 border-b border-border">
                          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2">
                            Appearance
                          </p>
                          {(() => {
                            const activeIdx = isSystemTheme ? 2 : theme === 'dark' ? 1 : 0;
                            const options = [
                              { icon: <SunIcon className="w-3.5 h-3.5" />, label: 'Light', action: () => setTheme('light') },
                              { icon: <MoonIcon className="w-3.5 h-3.5" />, label: 'Dark', action: () => setTheme('dark') },
                              { icon: <ComputerDesktopIcon className="w-3.5 h-3.5" />, label: 'Follow my device (auto light/dark)', action: useSystemTheme },
                            ];
                            return (
                              <>
                              <div className="relative flex bg-neutral-100 dark:bg-neutral-800 rounded-lg p-0.5">
                                <div
                                  className="absolute top-0.5 bottom-0.5 w-1/3 rounded-md bg-white dark:bg-neutral-600 shadow-sm transition-transform duration-200"
                                  style={{ transform: `translateX(calc(${activeIdx * 100}%))` }}
                                />
                                {options.map((opt, i) => (
                                  <button
                                    key={i}
                                    onClick={opt.action}
                                    title={opt.label}
                                    aria-label={opt.label}
                                    className={`relative z-10 flex-1 flex items-center justify-center py-1.5 text-xs font-medium transition-colors rounded-full ${
                                      activeIdx === i
                                        ? 'text-text-primary'
                                        : 'text-text-secondary hover:text-text-primary'
                                    }`}
                                  >
                                    {opt.icon}
                                  </button>
                                ))}
                              </div>
                              <p className="mt-1.5 text-[11px] font-medium text-text-secondary">
                                {isSystemTheme
                                  ? 'Matching your device — switches with its light/dark setting'
                                  : theme === 'dark'
                                    ? 'Always dark'
                                    : 'Always light'}
                              </p>
                              </>
                            );
                          })()}
                        </div>

                        {/* Members without the header gear reach their own settings
                            (profile, password, email notifications) from here. */}
                        {!can(PERM.TENANT_MANAGE) && (
                          <Link
                            to="/learn/settings"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-text-primary hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors border-b border-border"
                          >
                            <UserCircleIcon className="w-6 h-6" />
                            Account settings
                          </Link>
                        )}

                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-error-600 dark:text-error-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                        >
                          <ArrowRightStartOnRectangleIcon className="w-6 h-6" />
                          Log Out
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </header>

        <NotificationsPanel
          isOpen={notificationsPanelOpen}
          onClose={() => setNotificationsPanelOpen(false)}
        />

        {/* Page content. Capped to a shared max width and centered so every
            page lines up (like the Dashboard). */}
        <main className="flex-1 p-6 [--sticky-top:72px]">
          {/* Downgrade reconciliation: admins see the countdown / lock notice
              on every Manage page until they choose what stays. */}
          <ReconciliationBanner />
          {/* Invited members never see the signup questionnaire, so this is
              where they are shown the documents they are bound by (T&C §1.3).
              Informational — it never gates the page. */}
          <LegalAcknowledgementNotice />
          {/* Route-level crash containment: a page error must never unmount
              the whole app into a blank screen (prize-detail incident,
              2026-07-20). Keyed by path so navigating away resets it. */}
          <div className="mx-auto w-full max-w-7xl">
            <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
          </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>

      <AssistantLauncher />
    </div>
  );
};

export default AppLayout;
